import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  Button,
  Cascader,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Tooltip,
  Typography,
  Modal,
  Popover,
} from "antd";
import { InfoCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { ClientAutoComplete } from "../client-auto-complete";
import { MatterStatusSelectorDropdown } from "../matter-status-selector-dropdown";
import { MatterExternalMatterLink } from "../matter-external-matter-link";
import { DisplayOnlyField } from "../matter-client-and-matter-details";
import {
  ClientValue,
  ExternalMatter,
  ExternalMatterFeeEarner,
  ExternalMatterTaxStatus,
  FeeEarner,
  Matter,
  MatterFormMode,
  MatterNonExternalFieldsPreUpdate,
  MatterStatus,
  MatterTemplate,
  MatterType,
  PracticeArea,
  PricingLevel,
  PricingType,
  PricingTarget,
  TaxStatus,
  OfficeLocation,
} from "../../data-types";
import {
  cascaderValueFromFeeEarner,
  orgUnitCascaderValue,
  templateFormValuesToMatterData,
} from "../../utils";
import {
  PRICING_STRUCTURE_OPTIONS,
  pricingStructureToCascaderValue,
  cascaderValueToPricingStructure,
} from "../pricing-structure-options";
import {
  MatterCreationOrEditingFormRenderProps,
  MatterFieldsEditorRawFormValues,
  MatterFormValuesToData,
} from "./types";

import styles from "./matter-creation-or-editing-form.css";
import {
  Permission,
  useAllFeeEarnerLevelsAndFeeEarnersQuery,
  usePermissionCheck,
  useSettings,
} from "../../../hooks";
import { GlobalContext } from "../../../context/global-context";
import { useQuery } from "@apollo/client";
import { GET_CURRENCIES } from "~/graphql/query/currencies-query";
import { orgUnitFromCascadeValue } from "~/helpers/cascader-helpers";
import { useNavigate } from "react-router";
import { OFFICE_LOCATIONS_QUERY } from "~/graphql/query/office-locations-query";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["matter-editor-form"]: matterEditorForm,
  ["matter-attribute"]: matterAttribute,
  ["disabled"]: matterAttributeDisabled,
  ["fixed-non-form-row"]: fixedNonFormRow,
  ["warning"]: fixedNonFormRowWarningText,
  ["save-button-field"]: saveButtonField,
  ["noheight"]: noHeight,
} = styles as any;

const filterOption = (input: string, option?: { label: string }) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

export const MatterCreationOrEditingForm = <
  T extends MatterType,
  U extends MatterFormMode,
  V extends boolean
>({
  className,
  disabled: formDisabled,
  externalMatterData,
  form,
  practiceAreas,
  onClientSave,
  onFinish,
  onDelete,
  showDelete: showDeleteRaw,
  showSave: showSaveRaw,
  clientFilterData,
  matter,
  pmsName,
  template,
  mode,
  client,
  setCurrentClient,
  parentMatterId,
  selectedCurrencyId,
  allowChangeCurrency,
}: MatterCreationOrEditingFormRenderProps<T, U, V>): ReturnType<
  FC<MatterCreationOrEditingFormRenderProps<T, U, V>>
> => {
  const taxonomy = useTaxonomyLabel();
  const PA_LABEL = taxonomy.PracticeArea;
  const FE_LABEL = taxonomy.FeeEarner;
  const TEMPLATE_LABEL = taxonomy.Template;

  const { settings } = useSettings();
  const showDeletePermission = usePermissionCheck(Permission.UPDATE_CONFIG);

  const editMode = isEditMode(mode);
  const showSave = editMode ? showSaveRaw : false;
  const showDelete = editMode && showDeletePermission ? showDeleteRaw : false;

  const linkingToParentMatter = !!parentMatterId;

  const { feeEarnerLevelsAndFeeEarners } = useAllFeeEarnerLevelsAndFeeEarnersQuery(true, false);

  const { currentUser } = useContext(GlobalContext);

  const loggedInPaId = currentUser?.practiceAreaId ?? undefined;

  const { data: currencies } = useQuery(GET_CURRENCIES);

  const isMultiCurrencyInstance = (currencies?.currencies?.length ?? 0) > 1;
  const getBaseCurrency = () => {
    return currencies?.currencies.find((currency) => currency.baseCurrency)?.id;
  };

  const { data: officeLocations } = useQuery(OFFICE_LOCATIONS_QUERY);
  const taxOptions =
    officeLocations?.officeLocations.reduce(
      (acc: Set<string>, { taxLabel, taxLevelPercent }: OfficeLocation) => {
        if (!taxLabel && !taxLevelPercent) return acc;
        const uniqueKey = `${taxLabel || settings?.taxLabel}-${
          taxLevelPercent || settings?.taxLevelPercent
        }`;
        acc.add(uniqueKey);
        return acc;
      },
      new Set<string>()
    ) ?? new Set<string>();
  const isMultiTaxInstance = taxOptions.size > 1;
  const currentLocation = officeLocations?.officeLocations.find(
    (location) => location.id === currentUser?.officeLocationId
  );

  const {
    id: matterId,
    title = "",
    organisationalUnit,
    status,
    isParent,
    reference,
  } = editMode && matter
    ? matter
    : {
        id: undefined,
        title: undefined,
        organisationalUnit: undefined,
        status: undefined,
        reference: undefined,
        isParent: undefined,
      };

  const externalReference = (matter as Matter)?.externalReference || undefined;
  const leadFeeEarner = (matter as Matter)?.leadFeeEarner || undefined;

  const [selectedExternalMatter, setSelectedExternalMatter] = useState<ExternalMatter | null>(
    (matter as Matter)?.externalMatter || null
  );

  const externalMatter = (matter as Matter)?.externalMatter;

  useEffect(() => {
    form.setFieldValue("externalMatterId", selectedExternalMatter?.id || null);
  }, [selectedExternalMatter]);

  useEffect(() => {
    setSelectedExternalMatter(externalMatter);
  }, [externalMatter]);

  const isTemplate = template === "template";
  const externalMatterId = "template" === template ? null : selectedExternalMatter?.id || null;

  const matterTaxExempt = matter?.taxStatus === TaxStatus.MatterExempt;

  const matterExternalMatterTaxExempt = matter?.taxStatus === TaxStatus.ExternalMatterExempt;

  const externalMatterTaxStatus = externalMatter?.taxStatus;

  const disabled = formDisabled || !!externalMatterId;

  const displayValues = useMemo((): {
    title: string;
    externalReference: string | null;
    practiceArea: string | null;
    leadFeeEarner: FeeEarner | ExternalMatterFeeEarner | undefined;
    matterTaxExempt: boolean;
  } => {
    if (!disabled) {
      return {
        title: title,
        externalReference: externalReference || null,
        practiceArea: practiceAreaNameById(practiceAreas, organisationalUnit?.id || undefined),
        leadFeeEarner: leadFeeEarner,
        matterTaxExempt: matterTaxExempt,
      };
    }

    return {
      title: selectedExternalMatter?.title || "",
      externalReference: selectedExternalMatter?.externalReference || null,
      practiceArea: !selectedExternalMatter?.organisationalUnit?.id
        ? null
        : practiceAreaNameById(practiceAreas, selectedExternalMatter?.organisationalUnit.id),
      leadFeeEarner: selectedExternalMatter?.leadFeeEarner || undefined,
      matterTaxExempt:
        matterExternalMatterTaxExempt || matter?.taxStatus === TaxStatus.MatterExempt,
    };
  }, [
    disabled,
    selectedExternalMatter?.title,
    selectedExternalMatter?.externalReference,
    selectedExternalMatter?.organisationalUnit?.id,
    selectedExternalMatter?.leadFeeEarner,
    selectedExternalMatter?.id,
    practiceAreas,
    matterExternalMatterTaxExempt,
    matter?.taxStatus,
    title,
    externalReference,
    organisationalUnit?.id,
    leadFeeEarner,
    matterTaxExempt,
  ]);

  const navigate = useNavigate();

  const formValues = form.getFieldsValue(true);

  const initialCurrencyId = !isMultiCurrencyInstance
    ? undefined
    : selectedCurrencyId
    ? selectedCurrencyId
    : editMode
    ? selectedExternalMatter?.currency?.id || matter?.currency?.id
    : selectedExternalMatter?.currency?.id ||
      matter?.currency.id ||
      client?.currency?.id ||
      currentUser?.currencyId ||
      getBaseCurrency();

  const initialFormValues: MatterFieldsEditorRawFormValues<T, U> = useMemo(() => {
    const values = dataToFormValues<T, U>(
      formValues,
      template,
      practiceAreas,
      matter as any,
      client,
      loggedInPaId,
      externalMatter,
      editMode,
      initialCurrencyId,
      currentLocation
    );

    return values;
  }, [
    template,
    practiceAreas,
    matter,
    externalMatter,
    initialCurrencyId,
    displayValues,
    matter?.organisationalUnit.id,
  ]);

  useEffect(() => {
    form.setFieldsValue(initialFormValues as any);
  }, [initialFormValues, form]);

  const currentPricingStructure = Form.useWatch(["pricingStructure"], form);

  const [showModal, setShowModal] = useState(false);

  const hasPricingStructureChanged = useMemo(() => {
    return (
      currentPricingStructure &&
      initialFormValues.pricingStructure &&
      (currentPricingStructure[0] !== initialFormValues.pricingStructure[0] ||
        currentPricingStructure[1] !== initialFormValues.pricingStructure[1] ||
        currentPricingStructure[2] !== initialFormValues.pricingStructure[2])
    );
  }, [currentPricingStructure, initialFormValues.pricingStructure]);

  const showPricingStructureConfirm = (cb: () => void) => {
    confirm({
      width: 800,
      title: `Are you sure you want to continue?`,
      icon: <ExclamationCircleOutlined style={{ color: "red" }} />,
      content: `All pricing for this matter will be deleted and replaced with the new pricing structure.`,
      onOk() {
        cb();
      },
      onCancel() {},
    });
  };

  const formatAndDoOnFinish =
    showSave && onFinish
      ? (formValuesRaw?: any) => {
          const formValues: any = matterId
            ? {
                ...formValuesRaw,
                id: matterId,
              }
            : {
                ...formValuesRaw,
              };
          const matterUpdate: MatterNonExternalFieldsPreUpdate<T, U> = formValuesToData<T, U>(
            { ...formValues, client },
            template,
            mode
          );
          if (editMode && hasPricingStructureChanged) {
            showPricingStructureConfirm(async () => {
              await onFinish(matterUpdate as any);
            });
          } else if (formValuesRaw.matterCurrencyId !== initialFormValues.matterCurrencyId) {
            showConfirm(() => {
              onFinish(matterUpdate as any);
            });
          } else {
            onFinish(matterUpdate as any);
          }
        }
      : undefined;

  useEffect(() => {
    const {
      officeLocationId,
      practiceAreaId: initPracticeAreaId,
      ...restInitialFormValues
    } = initialFormValues;

    // If the form already has a practice area set (e.g., from clarifying questions),
    // do not override it with defaults when practice areas/options load.
    const currentPracticeArea = form.getFieldValue("practiceAreaId");
    const shouldPreserveExistingPracticeArea = Array.isArray(currentPracticeArea)
      ? currentPracticeArea.length > 0
      : Boolean(currentPracticeArea);

    const nextValues = {
      ...restInitialFormValues,
      ...(shouldPreserveExistingPracticeArea ? {} : { practiceAreaId: initPracticeAreaId }),
    };

    form.setFieldsValue(nextValues as any);
    if (form.getFieldValue("officeLocationId") === undefined) {
      form.setFieldsValue({
        officeLocationId: officeLocationId || currentLocation?.id,
      });
    }
  }, [initialFormValues]);

  const paOptions = practiceAreas
    .filter((x) => !x.parent?.id)
    .map((pa) => {
      return {
        value: pa.id,
        label: pa.name,
        children:
          practiceAreas
            .filter((x) => x.parent?.id === pa.id)
            .map((y) => ({ value: y.id, label: y.name })) || [],
      };
    });

  const { confirm } = Modal;
  const showConfirm = (cb: any) => {
    confirm({
      width: 800,
      title: `Are you sure you want to continue?`,
      icon: <ExclamationCircleOutlined style={{ color: "red" }} />,
      content: `Changing the currency for this matter will reset all pricing details.`,
      onOk() {
        cb();
      },
      onCancel() {},
    });
  };

  const handleOnFinish = () => {
    const vals = form.getFieldsValue(true);

    return formatAndDoOnFinish && formatAndDoOnFinish(vals);
  };

  const handleDeleteMatter = async () => {
    if (onDelete) {
      await onDelete();
      navigate(isTemplate ? "/administrator/library-content/matter-templates" : "/matters");
    }
  };

  return (
    <>
      <Form
        className={clsx(matterEditorForm, className && className)}
        form={form as any}
        layout="horizontal"
        onFinish={() => handleOnFinish()}
        initialValues={initialFormValues}
      >
        {"matter" === template && clientFilterData && (
          <Form.Item
            label="Client"
            name="client"
            labelCol={{ span: 6 }}
            labelAlign="left"
            style={
              clientFilterData.duplicateCheckResult.hasDuplicates ? { height: "70px" } : undefined
            }
            className={clsx(matterAttribute)}
            trigger="onChangeClient"
            required
            rules={[
              {
                required: true,
                message: "Please select a Client",
              },
            ]}
            shouldUpdate
          >
            <ClientAutoComplete
              disabled={linkingToParentMatter}
              value={initialFormValues?.client || client}
              onChangeClient={(v) => {
                form.setFieldsValue({
                  externalMatterId: undefined,
                  client: v,
                });
                setSelectedExternalMatter(null);
                setCurrentClient && setCurrentClient(v);

                if (onFinish && matter?.id) {
                  const clientOnlyUpdate = {
                    id: matter.id,
                    clientId: v?.id || null,
                  };

                  onFinish(clientOnlyUpdate as any);
                }
              }}
              onClientSave={onClientSave || (() => {})}
              onClear={() => {
                setCurrentClient?.(null);
                setSelectedExternalMatter(null);
                form.setFieldsValue({
                  externalMatterId: undefined,
                  client: undefined,
                });
              }}
              clientFilterData={clientFilterData}
              bordered
              pmsName={pmsName}
            />
          </Form.Item>
        )}
        {"matter" === template && (
          <Form.Item name="externalMatterId" className={noHeight}>
            <Input type="hidden" value={form.getFieldsValue(["externalMatterId"])} />
          </Form.Item>
        )}
        {"matter" === template && (
          <Form.Item
            label={`Link to existing ${pmsName} Matter`}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 18, offset: 6 }}
            labelAlign="left"
            valuePropName="externalMatterId"
            shouldUpdate
          >
            {() => {
              return externalMatterData?.data ? (
                <MatterExternalMatterLink
                  isEditMode={editMode}
                  selectedCurrencyCode={
                    currencies?.currencies.find(
                      (x) => form.getFieldValue("matterCurrencyId") === x.id
                    )?.code
                  }
                  client={form.getFieldValue("client") || (matter as Matter)?.client || null}
                  onChange={(emid) => {
                    const externalMatter = externalMatterData?.data.items.find(
                      (emd) => emd?.id === emid
                    );

                    if (externalMatter && emid) {
                      form.setFieldsValue({
                        externalMatterId: emid,
                        title: externalMatter.title,
                        practiceAreaId: externalMatter?.organisationalUnit?.id
                          ? orgUnitCascaderValue(
                              practiceAreas,
                              externalMatter.organisationalUnit.id
                            ) || undefined
                          : undefined,
                        // Update currency to match external matter in creation mode
                        ...(isMultiCurrencyInstance &&
                          !editMode &&
                          externalMatter.currency?.id && {
                            matterCurrencyId: externalMatter.currency.id,
                          }),
                      });

                      setSelectedExternalMatter(externalMatter);
                    } else {
                      form.setFieldsValue({
                        externalMatterId: undefined,
                        title: initialFormValues.title,
                        practiceAreaId: initialFormValues.practiceAreaId || undefined,
                        // Reset currency to initial value when clearing external matter in creation mode
                        ...(isMultiCurrencyInstance &&
                          !editMode && {
                            matterCurrencyId: initialCurrencyId,
                          }),
                      });
                      setSelectedExternalMatter(null);
                    }

                    handleOnFinish();
                  }}
                  disabled={!form.getFieldValue("client")?.id || status === MatterStatus.Live}
                  matter={matter as Matter}
                  externalMatterData={externalMatterData}
                  externalMatterId={(externalMatterId as number | null) || null}
                  pmsName={pmsName}
                  showSwitch={status !== MatterStatus.Live}
                />
              ) : null;
            }}
          </Form.Item>
        )}
        {disabled ? (
          <>
            <DisplayOnlyField
              label="Matter title"
              value={displayValues.title}
              className={fixedNonFormRow}
            />

            {displayValues.leadFeeEarner && (
              <DisplayOnlyField
                label={`Lead ${FE_LABEL}`}
                value={displayValues.leadFeeEarner.name}
                className={fixedNonFormRow}
              />
            )}
            {"matter" === template && (
              <DisplayOnlyField
                label={`${pmsName} Reference`}
                value={
                  displayValues.externalReference || (
                    <span className={fixedNonFormRowWarningText}>
                      Link a {pmsName} Matter first
                    </span>
                  )
                }
                className={fixedNonFormRow}
              />
            )}
            {displayValues.practiceArea && editMode ? (
              <DisplayOnlyField
                label={PA_LABEL}
                value={displayValues.practiceArea || ""}
                className={fixedNonFormRow}
              />
            ) : (
              <Form.Item
                shouldUpdate
                required
                label={PA_LABEL}
                name="practiceAreaId"
                labelCol={{ span: 6 }}
                labelAlign="left"
                rules={[
                  {
                    required: true,
                    message: `Please select a ${PA_LABEL}`,
                  },
                ]}
              >
                <Cascader
                  showSearch={true}
                  allowClear
                  placeholder={`Select ${PA_LABEL}`}
                  options={paOptions}
                />
              </Form.Item>
            )}

            {externalMatterTaxStatus && (
              <DisplayOnlyField
                label="External Matter Tax Status"
                value={externalMatterTaxStatusHumanReadable(externalMatterTaxStatus) || ""}
                className={fixedNonFormRow}
              />
            )}
          </>
        ) : (
          <>
            <Form.Item
              label="Matter Title"
              name="title"
              labelCol={{ span: 6 }}
              labelAlign="left"
              className={clsx(matterAttribute, disabled && matterAttributeDisabled)}
              rules={[
                {
                  required: true,
                  message: "Please enter a title",
                },
              ]}
            >
              <Input placeholder="Matter title" name="title" disabled={disabled} />
            </Form.Item>
            {"matter" === template && (
              <Form.Item
                label={`Lead ${FE_LABEL}`}
                name="leadFeeEarnerId"
                labelCol={{ span: 6 }}
                labelAlign="left"
                className={clsx(matterAttribute, disabled && matterAttributeDisabled)}
                rules={[
                  {
                    type: "array",
                    message: `Please select the lead ${FE_LABEL}`,
                  },
                ]}
              >
                <Cascader showSearch={true} options={feeEarnerLevelsAndFeeEarners} />
              </Form.Item>
            )}
            {"matter" === template && (
              <DisplayOnlyField
                label={`${pmsName} Reference`}
                value={
                  displayValues.externalReference || (
                    <span className={fixedNonFormRowWarningText}>
                      Link a {pmsName} Matter first
                    </span>
                  )
                }
                className={fixedNonFormRow}
              />
            )}
            <Form.Item
              shouldUpdate
              required
              label={PA_LABEL}
              name="practiceAreaId"
              labelCol={{ span: 6 }}
              labelAlign="left"
              className={clsx(matterAttribute, disabled && matterAttributeDisabled)}
              rules={[
                {
                  required: true,
                  message: `Please select a ${PA_LABEL}`,
                },
              ]}
            >
              <Cascader
                showSearch={true}
                allowClear
                disabled={disabled}
                placeholder={`Select ${PA_LABEL}`}
                options={paOptions}
              />
            </Form.Item>
          </>
        )}
        {!isTemplate && (
          <Form.Item
            label={
              <>
                Pricing structure
                <Popover
                  content={
                    <div style={{ maxWidth: "300px" }}>
                      <p>
                        <b>Bottom Up:</b> Use this option if you wish to create a proposal beginning
                        with scope, resourcing leverage and rates to calculate the fee that should
                        be charged.
                      </p>
                      <p>
                        <b>Top Down:</b> Use this option if you wish to create a proposal that
                        begins with your predetermined fee or predetermined number of hours which
                        you will then reverse engineer into resourcing leverage and rates to fit.
                      </p>
                    </div>
                  }
                >
                  <span>
                    {" "}
                    <InfoCircleOutlined style={{ padding: "5px 5px" }} />
                  </span>
                </Popover>
              </>
            }
            name="pricingStructure"
            labelCol={{ span: 6 }}
            labelAlign="left"
            rules={[
              {
                required: true,
                message: "Please select a pricing structure",
              },
            ]}
          >
            <Cascader
              options={PRICING_STRUCTURE_OPTIONS(FE_LABEL)}
              disabled={form.getFieldValue("copyPricing")}
            />
          </Form.Item>
        )}
        {editMode && matterId && !isTemplate && !parentMatterId && (
          <Form.Item
            label="Matter status"
            name="status"
            labelCol={{ span: 6 }}
            labelAlign="left"
            trigger="onUpdate"
            valuePropName="status"
          >
            <MatterStatusSelectorDropdown
              isParent={isParent}
              status={status}
              id={matterId}
              reference={reference}
              onUpdate={async (statusData) => {
                form.setFieldsValue({
                  status: statusData.status,
                } as any);

                handleOnFinish();
              }}
              type={template}
              externalMatterId={externalMatterId}
              pmsName={pmsName}
            />
          </Form.Item>
        )}
        {isMultiTaxInstance && !isTemplate && (
          <Form.Item
            label="Office location for tax purposes"
            name="officeLocationId"
            labelCol={{ span: 6 }}
            labelAlign="left"
            rules={[
              {
                required: true,
                message: "Please select an office location",
              },
            ]}
          >
            <Select
              showSearch
              filterOption={filterOption}
              options={Array.from(officeLocations?.officeLocations || [])
                .map((o) => {
                  return {
                    value: o.id,
                    label: o.name,
                  };
                })
                .sort((a, b) => a.label.localeCompare(b.label))}
            />
          </Form.Item>
        )}
        <Row gutter={16}>
          {isMultiCurrencyInstance && !isTemplate && (
            <Col span={8}>
              <Form.Item
                name="matterCurrencyId"
                label="Currency"
                labelCol={{ span: 10 }}
                labelAlign="left"
                wrapperCol={{ span: 12 }}
                required
                rules={[
                  {
                    required: true,
                    message: "Please select a Currency",
                  },
                ]}
              >
                <Select
                  disabled={!!selectedExternalMatter?.currency?.id || !allowChangeCurrency}
                  showSearch
                  filterOption={(input, option) => {
                    const currency = currencies?.currencies.find((c) => c.id === option?.value);
                    return (
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
                      (currency?.name ?? "").toLowerCase().includes(input.toLowerCase())
                    );
                  }}
                  options={currencies?.currencies
                    .map((x) => ({ label: x.code, value: x.id }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                ></Select>
              </Form.Item>
            </Col>
          )}
          {!isTemplate && (
            <Col span={7}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const matterStatus = form.getFieldValue("status");
                  const matterTaxExemptDisabled =
                    disabled ||
                    matterStatus === MatterStatus.Live ||
                    matterStatus === MatterStatus.Closed ||
                    matterStatus === MatterStatus.Lost ||
                    matter?.taxStatus === TaxStatus.ExternalMatterExempt;

                  return (
                    <Form.Item
                      label="Matter tax exempt"
                      name="matterTaxExempt"
                      className={clsx(
                        matterAttribute,
                        matterTaxExemptDisabled && matterAttributeDisabled
                      )}
                      valuePropName="checked"
                      tooltip={
                        matterExternalMatterTaxExempt &&
                        `The tax status of this matter is set as 'Tax Exempt' within ${pmsName} and can only be changed by your finance team`
                      }
                    >
                      <Switch disabled={matterTaxExemptDisabled} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item
              label={
                <>
                  Confidential{" "}
                  <Tooltip
                    placement="right"
                    title={
                      "Confidential matters will only be visible to their creator and collaborators"
                    }
                  >
                    <InfoCircleOutlined style={{ padding: "5px 5px" }} />
                  </Tooltip>
                </>
              }
              name="confidential"
              className={matterAttribute}
              valuePropName="checked"
            >
              {!!matter?.confidential && matter?.createdBy?.id !== currentUser?.userId ? (
                <Tooltip
                  title="Only the matter's creator can change this setting"
                  placement="right"
                >
                  <Switch disabled={true} defaultChecked />
                </Tooltip>
              ) : (
                <Switch disabled={false} />
              )}
            </Form.Item>
          </Col>
        </Row>
        {showSave && editMode && (
          <Form.Item className={`${matterAttribute} ${saveButtonField}`}>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        )}
        {showDelete && editMode && (
          <Form.Item>
            {selectedExternalMatter != null ? (
              <Tooltip title={"Matter must be unlinked before deleting"} placement="right">
                <Button type="primary" danger onClick={() => setShowModal(true)} disabled={true}>
                  {isTemplate ? `Delete ${TEMPLATE_LABEL}` : "Delete Proposal"}
                </Button>
              </Tooltip>
            ) : (
              <Button type="primary" danger onClick={() => setShowModal(true)}>
                {isTemplate ? `Delete ${TEMPLATE_LABEL}` : "Delete Proposal"}
              </Button>
            )}
          </Form.Item>
        )}
      </Form>
      <Modal
        title={isTemplate ? `Delete ${TEMPLATE_LABEL}` : "Delete Proposal"}
        open={showModal}
        onOk={() => {
          handleDeleteMatter();
          setShowModal(false);
        }}
        onCancel={() => {
          setShowModal(false);
        }}
        okText="Delete"
      >
        <Typography.Text>
          Are you sure you want to delete {matter ? `"${matter.title}"` : "this proposal"}?
        </Typography.Text>
      </Modal>
    </>
  );
};

const practiceAreaNameById = (practiceAreas: PracticeArea[], id?: number): string | null => {
  const result =
    practiceAreas.length > 0
      ? id
        ? practiceAreas.find((pa) => pa.id === id)?.name ?? null
        : practiceAreas[0].name ?? null
      : null;

  return result;
};

const dataToFormValues = <T extends MatterType, U extends MatterFormMode>(
  formValues: any,
  template: T,
  practiceAreas: PracticeArea[],
  matter?: U extends MatterFormMode.Edit ? (T extends "template" ? MatterTemplate : Matter) : never,
  client?: ClientValue | null,
  defaultPaId?: number,
  externalMatter?: ExternalMatter | null,
  isEdit?: boolean,
  currencyId?: number | null,
  currentLocation?: OfficeLocation
): MatterFieldsEditorRawFormValues<T, U> => {
  const formData = templateFormValuesToMatterData(formValues);

  const constPaId = isEdit
    ? matter?.organisationalUnit.id
    : defaultPaId || matter?.organisationalUnit.id;

  const paId = orgUnitCascaderValue(practiceAreas, constPaId);

  const valueEntity = {
    ...formData,
    ...(matter?.title && { title: matter.title }),
    ...((matter?.organisationalUnit.id || defaultPaId) && {
      practiceAreaId: paId,
    }),
    ...(matter?.status && {
      status: matter.status,
    }),
    ...(matter?.pricingLevel && {
      pricingLevel: matter.pricingLevel,
    }),
    ...(matter?.pricingType && {
      pricingType: matter.pricingType,
    }),
    ...(matter?.pricingTarget && {
      pricingTarget: matter.pricingTarget,
    }),
    ...(matter?.taxStatus && {
      matterTaxExempt: matter.taxStatus === TaxStatus.MatterExempt,
    }),
    ...(currencyId && {
      matterCurrencyId: currencyId,
    }),
    ...(matter?.officeLocation
      ? { officeLocationId: matter.officeLocation.id }
      : !isEdit
      ? { officeLocationId: currentLocation?.id }
      : {}),
    ...(matter?.confidential !== null && {
      confidential: matter?.confidential,
    }),
  };

  const {
    title = "",
    practiceAreaId = undefined,
    status = undefined,
    pricingLevel = PricingLevel.None,
    pricingType = PricingType.None,
    pricingTarget = null,
    matterTaxExempt = false,
    officeLocationId = undefined,
    confidential = false,
  } = valueEntity || {
    title: "",
    practiceAreaId: undefined,
    status: undefined,
    pricingType: PricingType.None,
    pricingLevel: PricingLevel.None,
    pricingTarget: null,
    matterTaxExempt: false,
    officeLocationId: undefined,
    confidential: false,
  };

  const baseValues = {
    title: title,
    status,
    pricingType: pricingType,
    pricingLevel: pricingLevel,
    pricingTarget: pricingTarget,
    pricingStructure: pricingStructureToCascaderValue({
      pricingType,
      pricingLevel,
      pricingTarget: pricingTarget ?? PricingTarget.Fee,
    }),
    matterTaxExempt: matterTaxExempt,
    practiceAreaId,
    matterCurrencyId: currencyId ?? undefined,
  };
  if ("template" === template) {
    return baseValues as MatterFieldsEditorRawFormValues<T, U>;
  }

  const { client: clientValueEntity, leadFeeEarner } = (valueEntity as Partial<Matter>) || {
    externalMatter: undefined,
    client: undefined,
    leadFeeEarner: undefined,
  };

  const leadFeeEarnerVal = cascaderValueFromFeeEarner(
    (matter as Matter)?.leadFeeEarner || leadFeeEarner
  );

  const convertedToForm = {
    ...baseValues,
    externalMatterId: isEdit ? externalMatter?.id : formValues.externalMatterId,
    ...(clientValueEntity && {
      client: clientValueEntity,
    }),
    ...((matter as Matter)?.client && {
      client: (matter as Matter).client,
    }),
    ...((client || client === null) && {
      client: client,
    }),
    ...(leadFeeEarnerVal && {
      leadFeeEarnerId: leadFeeEarnerVal,
    }),
    ...(officeLocationId && {
      officeLocationId: officeLocationId,
    }),
    ...(confidential !== null && {
      confidential: confidential,
    }),
  } as any;

  return convertedToForm;
};

export const formValuesToData = <T extends MatterType, U extends MatterFormMode>(
  props: MatterFormValuesToData<T, U>,
  template: T,
  mode: MatterFormMode
): MatterNonExternalFieldsPreUpdate<T, U> => {
  const pricingStructure = cascaderValueToPricingStructure(props.pricingStructure ?? []);

  let baseMatterUpdateValues = {
    title: props.title,
    matterTaxExempt: props.matterTaxExempt,
    practiceAreaId: orgUnitFromCascadeValue(props.practiceAreaId),
    officeLocationId: props.officeLocationId,
    confidential: props.confidential,
    pricingType: pricingStructure?.pricingType,
    pricingLevel: pricingStructure?.pricingLevel,
    pricingTarget: pricingStructure?.pricingTarget,
    ...(MatterFormMode.Edit === mode && {
      id: props.id,
      status: props.status || MatterStatus.Draft,
    }),
    ...(MatterFormMode.Create === mode && {
      multiDisciplinaryMatter: props.multiDisciplinaryMatter,
      workspaceTitle: props.workspaceTitle,
      parentPhaseId: props.parentPhaseId,
      matterCurrencyId: props.matterCurrencyId,
    }),
  } as MatterNonExternalFieldsPreUpdate<T, U>;

  if ("template" === template) {
    return baseMatterUpdateValues as MatterNonExternalFieldsPreUpdate<T, U>;
  }

  baseMatterUpdateValues = {
    ...baseMatterUpdateValues,
    ...(MatterFormMode.Create === mode && {
      templateId: props.templateId,
      historicalMatterId: props.historicalMatterId,
      copyScope: props.copyScope,
      copyResourcing: props.copyResourcing,
      copyPricing: props.copyPricing,
    }),
    practiceAreaId: orgUnitFromCascadeValue(props.practiceAreaId),
    leadFeeEarnerId: null,
    externalMatterId: props?.externalMatterId ? props!.externalMatterId : null,
    clientId: props?.client ? props!.client!.id : null,
    multiDisciplinaryMatter: props.multiDisciplinaryMatter,
    workspaceTitle: props.workspaceTitle,
    parentPhaseId: props.parentPhaseId,
    matterCurrencyId: props.matterCurrencyId,
  } as MatterNonExternalFieldsPreUpdate<T, U>;

  if (props?.leadFeeEarnerId) {
    baseMatterUpdateValues = {
      ...baseMatterUpdateValues,
      leadFeeEarnerId:
        props?.leadFeeEarnerId.length > 1 ? props?.leadFeeEarnerId[1] : props?.leadFeeEarnerId[0],
    } as MatterNonExternalFieldsPreUpdate<T, U>;
  }
  return baseMatterUpdateValues as MatterNonExternalFieldsPreUpdate<T, U>;
};

const isEditMode = (mode: MatterFormMode): boolean => {
  return mode === MatterFormMode.Edit;
};

const externalMatterTaxStatusHumanReadable = (
  taxStatus: ExternalMatterTaxStatus | undefined
): string => {
  const humanReadableTaxStatus = "No data";

  if (taxStatus) {
    if (taxStatus === ExternalMatterTaxStatus.Exempt) {
      return "Exempt";
    } else if (taxStatus === ExternalMatterTaxStatus.NotExempt) {
      return "Not exempt";
    }
  }

  return humanReadableTaxStatus;
};
